import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

/** 2–10, then J Q K A. Suits don't matter in either of these games. */
export const RANKS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14] as const;

export const RANK_LABEL: Record<number, string> = {
	11: "J",
	12: "Q",
	13: "K",
	14: "A",
};

export function rankLabel(rank: number): string {
	return RANK_LABEL[rank] ?? String(rank);
}

/** Four of every rank, shuffled. */
export function freshDeck(rng: Rng): number[] {
	const deck: number[] = [];
	for (const rank of RANKS) for (let i = 0; i < 4; i++) deck.push(rank);
	return rng.shuffle(deck);
}

// --- War of Cards ---------------------------------------------------------

export const WAR_ROUNDS = 12;
export const WAR_REVEAL_MS = 2200;

export interface WarState extends BaseState {
	kind: "war";
	/** Emptied before being sent — only the counts go out. */
	decks: [number[], number[]];
	counts: [number, number];
	round: number;
	phase: "flip" | "reveal";
	flipped: [number | null, number | null];
	/** Cards carried over from a tie. */
	pot: number;
	lastRound: { winner: PlayerIndex | null; because: string } | null;
	endsAt: number;
}

export interface WarMove {
	flip: true;
}

function judgeWar(state: WarState, now: number): WarState {
	const [a, b] = state.flipped;
	const scores: [number, number] = [...state.scores];
	// Only cards actually flipped join the pot, so the totals always add up.
	let pot = state.pot + (a === null ? 0 : 1) + (b === null ? 0 : 1);
	let lastRound: WarState["lastRound"];

	if (a === null && b === null) {
		// Neither flipped — there is nothing to judge, and nothing to award.
		// Whatever is on the table stays there for the next round.
		lastRound = { winner: null, because: "Nobody flipped — the cards wait." };
	} else if (a === null || b === null) {
		// Someone never flipped; the other takes the lot.
		const winner: PlayerIndex = a === null ? 1 : 0;
		scores[winner] += pot;
		lastRound = { winner, because: "Too slow to flip!" };
		pot = 0;
	} else if (a === b) {
		// A tie leaves the cards on the table for the next round.
		lastRound = { winner: null, because: `Both played ${rankLabel(a)} — war! Cards carry over.` };
	} else {
		const winner: PlayerIndex = a > b ? 0 : 1;
		scores[winner] += pot;
		lastRound = {
			winner,
			because: `${rankLabel(Math.max(a, b))} beats ${rankLabel(Math.min(a, b))}`,
		};
		pot = 0;
	}

	return { ...state, scores, pot, lastRound, phase: "reveal", endsAt: now + WAR_REVEAL_MS };
}

function nextWarRound(state: WarState, now: number): WarState {
	const round = state.round + 1;
	if (round >= WAR_ROUNDS || state.decks[0].length === 0 || state.decks[1].length === 0) {
		// A pot still on the table belongs to nobody, and letting it vanish
		// could flip a one-card margin. Split it down the middle: only ties
		// carry a pot forward, two cards at a time, so it is always even.
		const half = Math.floor(state.pot / 2);
		const scores: [number, number] = [state.scores[0] + half, state.scores[1] + half];
		return { ...state, scores, pot: 0, result: resultFromScores(scores), endsAt: now };
	}
	return {
		...state,
		round,
		phase: "flip",
		flipped: [null, null],
		lastRound: null,
		endsAt: now + 12_000,
	};
}

export const warOfCards: GameDefinition<WarState, WarMove> = {
	id: "war",
	category: "cards",
	title: "War of Cards",
	emoji: "⚔️",
	blurb: "Flip a card each. Higher card takes the pile.",
	howTo: [
		"You both flip a card at the same time.",
		"The higher card wins both cards.",
		"Same card? They stay on the table and go to whoever wins the next flip.",
		"Most cards after twelve rounds wins.",
	],

	create(rng, now) {
		const deck = freshDeck(rng);
		const half = Math.floor(deck.length / 2);
		return {
			kind: "war",
			decks: [deck.slice(0, half), deck.slice(half)],
			counts: [half, deck.length - half],
			round: 0,
			phase: "flip",
			flipped: [null, null],
			pot: 0,
			lastRound: null,
			endsAt: now + 12_000,
			// Both players flip at once.
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		return (raw as { flip?: unknown }).flip === true ? { flip: true } : null;
	},

	apply(state, player, _move, now): MoveOutcome<WarState> {
		if (state.result) return reject("This game is already finished.");
		if (state.phase !== "flip") return reject("Hold on — the cards are showing.");
		if (state.flipped[player] !== null) return reject("You've already flipped.");
		const deck = state.decks[player];
		if (deck.length === 0) return reject("You've run out of cards!");

		const decks: [number[], number[]] = [[...state.decks[0]], [...state.decks[1]]];
		const card = decks[player].pop() ?? 0;
		const flipped: [number | null, number | null] = [...state.flipped];
		flipped[player] = card;

		const next: WarState = {
			...state,
			decks,
			counts: [decks[0].length, decks[1].length],
			flipped,
		};
		return {
			ok: true,
			state: flipped[0] !== null && flipped[1] !== null ? judgeWar(next, now) : next,
		};
	},

	legalMoves(state, player) {
		if (state.result || state.phase !== "flip") return [];
		if (state.flipped[player] !== null || state.decks[player].length === 0) return [];
		return [{ flip: true } as WarMove];
	},

	tick(state, now) {
		if (state.result || now < state.endsAt) return null;
		return state.phase === "flip" ? judgeWar(state, now) : nextWarRound(state, now);
	},

	nextTickAt(state) {
		return state.result ? null : state.endsAt;
	},

	redact(state) {
		// The decks are face down: only how many are left is public.
		return { ...state, decks: [[], []] };
	},
};

// --- Speed Snap -----------------------------------------------------------

export const SNAP_FLIP_MS = 1600;
export const SNAP_CARDS = 30;
/** Snapping when there is no pair hands the point to the other player. */
export const SNAP_PENALTY = 1;
/**
 * Every deck is arranged to hold at least this many snappable pairs. Four
 * pairs in a fifty-second game left 43% of bot races tied; seven gives the
 * quicker player more chances to pull ahead — and being odd, an exact split
 * of the planted pairs is impossible, so a game of clean snaps cannot tie.
 */
export const SNAP_MIN_PAIRS = 7;

export interface SnapState extends BaseState {
	kind: "snap";
	deck: number[];
	/** The two most recent cards, newest first. */
	shown: number[];
	/** Set while a real pair is on the table and nobody has snapped yet. */
	pairUp: boolean;
	/** The flip on which the pair now showing was snapped, if it was. */
	pairTakenAtFlip: number | null;
	endsAt: number;
	lastEvent: { seat: PlayerIndex | null; because: string } | null;
	flips: number;
}

export interface SnapMove {
	snap: true;
}

/** Adjacent equal ranks in flip order — the cards come off the end. */
function countAdjacentPairs(deck: number[]): number {
	let pairs = 0;
	for (let i = 1; i < deck.length; i++) {
		if (deck[i] === deck[i - 1]) pairs++;
	}
	return pairs;
}

/**
 * A straight shuffle of 30 cards over 13 ranks leaves about one game in six
 * with no pair at all — a whole game whose winning move never exists. So after
 * shuffling, pull twins together until enough pairs are planted. Every choice
 * comes from the same generator, so the deck is still a pure function of the
 * seed.
 */
function plantSnapPairs(deck: number[], rng: Rng): number[] {
	const cards = [...deck];
	for (let guard = 0; countAdjacentPairs(cards) < SNAP_MIN_PAIRS && guard < 200; guard++) {
		// Pick a card at random; if it has a twin somewhere that isn't already
		// beside it, swap that twin into the next slot along.
		const at = rng.int(cards.length - 1);
		const twin = cards.findIndex((card, index) => card === cards[at] && Math.abs(index - at) > 1);
		if (twin === -1) continue;
		const beside = at + 1;
		[cards[beside], cards[twin]] = [cards[twin], cards[beside]];
	}
	return cards;
}

export const speedSnap: GameDefinition<SnapState, SnapMove> = {
	id: "snap",
	category: "cards",
	title: "Speed Snap",
	emoji: "👏",
	blurb: "Cards flip one by one. Spot two the same and snap first!",
	howTo: [
		"Cards turn over one at a time, all by themselves.",
		"When two cards in a row match, tap SNAP as fast as you can.",
		"Snapping when there's no pair gives your friend a point.",
		"Most points when the deck runs out wins.",
	],

	create(rng, now) {
		const deck = plantSnapPairs(freshDeck(rng).slice(0, SNAP_CARDS), rng);
		return {
			kind: "snap",
			deck,
			shown: [],
			pairUp: false,
			pairTakenAtFlip: null,
			endsAt: now + SNAP_FLIP_MS,
			lastEvent: null,
			flips: 0,
			// Both players are watching the same pile.
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		return (raw as { snap?: unknown }).snap === true ? { snap: true } : null;
	},

	apply(state, player, _move, now): MoveOutcome<SnapState> {
		if (state.result) return reject("This game is already finished.");

		const scores: [number, number] = [...state.scores];
		if (state.pairUp) {
			scores[player] += 1;
			return {
				ok: true,
				state: {
					...state,
					scores,
					pairUp: false,
					pairTakenAtFlip: state.flips,
					lastEvent: { seat: player, because: "Snap! Point to you." },
					// Straight on to the next card.
					endsAt: now + SNAP_FLIP_MS,
				},
			};
		}

		// The other player snapped the same pair a moment earlier: being second
		// to a real pair is slow, not wrong, so it costs nothing.
		if (state.pairTakenAtFlip === state.flips) {
			return { ok: true, state };
		}

		const other: PlayerIndex = player === 0 ? 1 : 0;
		scores[other] += SNAP_PENALTY;
		return {
			ok: true,
			state: {
				...state,
				scores,
				lastEvent: { seat: player, because: "No pair — point to the other player!" },
			},
		};
	},

	legalMoves(state) {
		if (state.result) return [];
		// Snapping is always allowed; being wrong is the risk.
		return state.shown.length > 0 ? [{ snap: true } as SnapMove] : [];
	},

	tick(state, now) {
		if (state.result || now < state.endsAt) return null;

		const deck = [...state.deck];
		const card = deck.pop();
		if (card === undefined) {
			return { ...state, result: resultFromScores(state.scores), pairUp: false };
		}

		const shown = [card, ...state.shown].slice(0, 2);
		return {
			...state,
			deck,
			shown,
			flips: state.flips + 1,
			// A pair is only "live" until somebody snaps it or it scrolls away.
			pairUp: shown.length === 2 && shown[0] === shown[1],
			endsAt: now + SNAP_FLIP_MS,
			lastEvent: null,
		};
	},

	nextTickAt(state) {
		return state.result ? null : state.endsAt;
	},

	redact(state) {
		// What's coming next is nobody's business.
		return { ...state, deck: [] };
	},
};
